/**
 * Language-aware routing utilities for SEO-friendly URLs
 */

import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';

// Supported languages
export const supportedLanguages = [
  'en', 'zh-CN', 'zh-TW', 'fr', 'de', 'id', 'it', 'ja', 'ko', 'pt-BR', 'es-ES', 'es-MX', 'ar'
];

// Language code mapping (URL-friendly codes)
export const languageUrlMap: Record<string, string> = {
  'en': 'en',
  'zh-CN': 'zh-cn',
  'zh-TW': 'zh-tw',
  'fr': 'fr',
  'de': 'de',
  'id': 'id',
  'it': 'it',
  'ja': 'ja',
  'ko': 'ko',
  'pt-BR': 'pt-br',
  'es-ES': 'es',
  'es-MX': 'es-mx',
  'ar': 'ar'
};

// Reverse mapping (URL code -> i18n code)
export const urlToLanguageMap: Record<string, string> = {
  'en': 'en',
  'zh-cn': 'zh-CN',
  'zh-tw': 'zh-TW',
  'fr': 'fr',
  'de': 'de',
  'id': 'id',
  'it': 'it',
  'ja': 'ja',
  'ko': 'ko',
  'pt-br': 'pt-BR',
  'es': 'es-ES',
  'es-mx': 'es-MX',
  'ar': 'ar'
};

/**
 * Get language code from URL or current i18n language
 */
export const getLanguageFromUrl = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  // Check if first segment is a language code
  if (firstSegment && urlToLanguageMap[firstSegment.toLowerCase()]) {
    return urlToLanguageMap[firstSegment.toLowerCase()];
  }
  
  return 'en'; // Default
};

/**
 * Get URL-friendly language code
 */
export const getUrlLanguageCode = (lang: string): string => {
  return languageUrlMap[lang] || 'en';
};

/**
 * Get i18n language code from URL code
 */
export const getI18nLanguageCode = (urlCode: string): string => {
  return urlToLanguageMap[urlCode.toLowerCase()] || 'en';
};

/**
 * Generate language-aware URL
 * @param path - Path without language prefix (e.g., '/certifications', '/exam/123/name')
 * @param lang - Optional language code, defaults to current language
 * @returns URL with language prefix (e.g., '/en/certifications', '/fr/certifications')
 */
export const getLocalizedPath = (path: string, lang?: string): string => {
  const currentLang = lang || localStorage.getItem('i18nextLng') || 'en';
  const urlLang = getUrlLanguageCode(currentLang);
  
  // Don't add language prefix for English (default)
  if (urlLang === 'en') {
    return path;
  }
  
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  return `/${urlLang}/${cleanPath}`;
};

/**
 * Remove language prefix from path
 * @param path - Path with or without language prefix
 * @returns Path without language prefix
 */
export const removeLanguagePrefix = (path: string): string => {
  const segments = path.split('/').filter(Boolean);
  
  // Check if first segment is a language code
  if (segments.length > 0 && urlToLanguageMap[segments[0].toLowerCase()]) {
    return '/' + segments.slice(1).join('/');
  }
  
  return path.startsWith('/') ? path : '/' + path;
};

/**
 * Hook to get current language from URL
 */
export const useLanguageFromUrl = (): string => {
  const location = useLocation();
  return getLanguageFromUrl(location.pathname);
};

